import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Store, DollarSign, Settings, XCircle } from "lucide-react";

// Configuración de la conexión al backend - CORREGIDA
const API_BASE_URL = "https://supersofia.nvirred.com/consulta.php";

const HomePage = () => {
  const [paymentPoints, setPaymentPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentPoints = async () => {
      setLoading(true);
      try {
        // URL corregida con el formato de parámetros que usa tu backend
        const response = await fetch(`${API_BASE_URL}?route=payment-points`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setPaymentPoints(data);
        setError(null);
      } catch (err) {
        setError(
          "Error al cargar los puntos de venta. Asegúrate de que tu backend esté funcionando y sirviendo los datos."
        );
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentPoints();
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">
            Cargando puntos de venta...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-50 to-pink-50">
        <div className="text-center max-w-sm">
          <XCircle className="w-16 h-16 text-red-500 mb-4 mx-auto" />
          <p className="text-red-600 text-lg font-semibold mb-2">¡Oops!</p>
          <p className="text-gray-600 text-sm leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Header compacto para móviles */}
      <motion.div
        className="flex-shrink-0 px-4 pt-6 pb-4 sm:pt-8 sm:pb-6"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 text-center leading-tight">
          Selecciona tu{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            Punto de Venta
          </span>
        </h1>
      </motion.div>

      {/* Contenido principal que se adapta al espacio restante */}
      <div className="flex-1 flex items-center justify-center px-4 pb-6 overflow-hidden">
        {paymentPoints.length === 0 ? (
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center border border-gray-200 max-w-sm w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            <Store className="w-16 h-16 text-gray-400 mb-4 mx-auto" />
            <p className="text-gray-600 text-base mb-6">
              No hay puntos de venta disponibles. ¡Contacta al administrador!
            </p>
            <Link
              to="/admin"
              className="inline-block bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold py-3 px-6 rounded-full shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200 text-sm"
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Acceder como Admin
            </Link>
          </motion.div>
        ) : (
          <div className="w-full max-w-4xl">
            {/* Grid responsive que se adapta al espacio disponible */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 h-full">
              {paymentPoints.map((point, index) => (
                <motion.div
                  key={point.id}
                  className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col items-center text-center border border-gray-200 hover:shadow-xl transition-all duration-300 min-h-0"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index + 0.3, duration: 0.4 }}
                >
                  <Store className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500 mb-3 flex-shrink-0" />
                  
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                    {point.name}
                  </h2>
                  
                  <p className="text-gray-600 text-sm sm:text-base mb-4 line-clamp-2 flex-1">
                    {point.description}
                  </p>
                  
                  <Link
                    to={`/point/${point.id}`}
                    className="bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold py-2.5 px-5 sm:py-3 sm:px-6 rounded-full shadow-lg hover:shadow-xl active:scale-95 transition-all duration-200 flex items-center gap-2 text-sm sm:text-base mt-auto"
                  >
                    <DollarSign className="w-4 h-4" />
                    Ir a Cobrar
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default HomePage;